import { useState } from "react";
import type { MouseEvent, ReactNode } from "react";
import type { Listing } from "../types/domain";
import { money } from "../lib/format";
import { conditionLabel, isConditionApplicableToListing } from "../lib/categories";
import { Icon } from "./Icon";

type Props = {
  listing: Listing;
  go: (route: string, params?: Record<string, string>) => void;
  mode?: "grid" | "row";
  onMessage?: (listing: Listing) => void;
  showFavorite?: boolean;
  isFavorite?: boolean;
  favoriteBusy?: boolean;
  onFavoriteToggle?: (listing: Listing) => void;
  rowExtraActions?: ReactNode;
};

export function ListingCard({
  listing,
  go,
  mode = "grid",
  onMessage,
  showFavorite = false,
  isFavorite = false,
  favoriteBusy = false,
  onFavoriteToggle,
  rowExtraActions
}: Props) {
  const [phoneShown, setPhoneShown] = useState(false);
  const conditionApplicable = isConditionApplicableToListing(listing);
  const photo = listing.photos?.sort((a, b) => a.sort_order - b.sort_order)[0];
  const ethicalTag = listing.ethical_tags?.[0];
  const rowBadge = ethicalTag || (conditionApplicable ? conditionLabel(listing.item_condition) : null);
  const openListing = (event: MouseEvent) => {
    event.preventDefault();
    go("listing", { id: String(listing.id) });
  };
  const favoriteButton = showFavorite ? (
    <button
      className={`favorite-toggle${isFavorite ? " active" : ""}`}
      type="button"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onFavoriteToggle?.(listing);
      }}
      disabled={favoriteBusy}
      aria-pressed={isFavorite}
      aria-label={isFavorite ? "Удалить из избранного" : "Добавить в избранное"}
      title={isFavorite ? "В избранном" : "Добавить в избранное"}
    >
      <Icon name="heart" />
    </button>
  ) : null;

  if (mode === "row") {
    return (
      <article className="listing-card listing-card-row">
        <div className="listing-card-media">
          <a className="listing-image" href={`/listing/${listing.id}`} onClick={openListing}>
            <img src={photo?.thumb_url || photo?.url || "/assets/placeholder.png"} alt="" />
            {rowBadge ? <span>{rowBadge}</span> : null}
          </a>
          {favoriteButton}
        </div>
        <div className="listing-row-main">
          <a className="listing-title" href={`/listing/${listing.id}`} onClick={openListing}>{listing.title}</a>
          <strong>{money(listing.price, listing.currency)}</strong>
          <small>{listing.city}{listing.region ? `, ${listing.region}` : ""}</small>
          <p>{listing.description}</p>
          <span className="muted">
            {listing.leaf_category?.name || listing.subcategory?.name || listing.category?.name}
            {conditionApplicable ? ` · ${conditionLabel(listing.item_condition)}` : ""}
          </span>
        </div>
        <aside className="listing-row-side">
          <strong>{listing.profile?.display_name ?? "Продавец"}</strong>
          <span className="muted">Рейтинг {listing.profile?.rating ?? 0} · отзывов {listing.profile?.reviews_count ?? 0}</span>
          {phoneShown && listing.phone_visible && listing.phone ? <a className="phone-link" href={`tel:${listing.phone}`}>{listing.phone}</a> : null}
          <button className="btn btn-ghost" type="button" onClick={() => setPhoneShown(true)}>
            {listing.phone_visible ? (phoneShown ? "Телефон показан" : "Показать телефон") : "Телефон скрыт"}
          </button>
          {onMessage ? <button className="btn btn-primary" type="button" onClick={() => onMessage(listing)}>Написать</button> : null}
          {rowExtraActions}
        </aside>
      </article>
    );
  }

  return (
    <article className="listing-card">
      <div className="listing-card-media">
        <a className="listing-image" href={`/listing/${listing.id}`} onClick={openListing}>
          <img src={photo?.thumb_url || photo?.url || "/assets/placeholder.png"} alt="" />
          {ethicalTag ? <span>{ethicalTag}</span> : null}
        </a>
        {favoriteButton}
      </div>
      <div className="listing-body">
        <a className="listing-title" href={`/listing/${listing.id}`} onClick={openListing}>{listing.title}</a>
        <strong>{money(listing.price, listing.currency)}</strong>
        <small>{listing.city}{listing.region ? `, ${listing.region}` : ""}</small>
      </div>
    </article>
  );
}
